import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useSpring, useMotionValue, useTransform } from "framer-motion";
import { BrowserRouter, Link, Route, Routes, useLocation } from "react-router-dom";
import { ArrowUpRight, ChevronDown, MoveRight, Menu, X, Plus, Minus, ShieldCheck, Lock, FileText, Download, Pin } from "lucide-react";
import { Toaster, toast } from "sonner";
import "@/App.css";

const API = process.env.REACT_APP_BACKEND_URL;

// Official registration form link (e.g. Google Form). When set, all Register
// buttons open it in a new tab; until then they open the on-site enquiry modal.
const REGISTER_URL = "";

const PHOTOS = {
  "Jitisha": "https://customer-assets-wrfwihn1.emergentagent.net/job_hpsmun/artifacts/f7761bdfb42a1822_jitisha-mun.jpeg",
  "Aakash Nambiar": "https://customer-assets-wrfwihn1.emergentagent.net/job_hpsmun/artifacts/7f2094169ff8b49e_aakash-mun.jpeg",
  "Anish Verma": "https://customer-assets-wrfwihn1.emergentagent.net/job_hpsmun/artifacts/6099ed42cb3858c1_anish-mun.jpeg",
  "Antra": "https://customer-assets-wrfwihn1.emergentagent.net/job_hpsmun/artifacts/b30be181d028a63d_antra-mun.jpeg",
  "Karmeest Sonthalia": "https://customer-assets-wrfwihn1.emergentagent.net/job_hpsmun/artifacts/d0ba6ea92b3e92ef_karmeest-mun.jpeg",
  "Sai Advaith Voopalanchi": "https://customer-assets-wrfwihn1.emergentagent.net/job_hpsmun/artifacts/5bd4dd6b3fd282e3_advaith-mun.jpeg",
  "Sharanya Varshney": "https://customer-assets-wrfwihn1.emergentagent.net/job_hpsmun/artifacts/17a2374c1ca76633_sharanya-mun.jpeg",
  "Saurabh Alivellu": "https://customer-assets-wrfwihn1.emergentagent.net/job_hpsmun/artifacts/003d529da47d9890_saurabh-mun.jpeg",
  "Laksh Mahankali": "https://customer-assets-wrfwihn1.emergentagent.net/job_hpsmun/artifacts/78ac98ad0c57333f_laksh-mun.jpeg",
  "Aaryaveer Gupta": "https://customer-assets-wrfwihn1.emergentagent.net/job_hpsmun/artifacts/24b2f15089c63cb6_aaryaveer-mun.jpeg",
  "Akshaya Tummala": "https://customer-assets-wrfwihn1.emergentagent.net/job_hpsmun/artifacts/4eeb403903e91a7b_akshaya-mun.jpeg",
  "Siya Thakkar": "https://customer-assets-wrfwihn1.emergentagent.net/job_hpsmun/artifacts/961bf903b608d9f0_siya-mun.jpeg",
  "Riyans Anthigari": "https://customer-assets-wrfwihn1.emergentagent.net/job_hpsmun/artifacts/804e1d540ea62f1c_riyans-mun.jpeg",
  "Shlok Walia": "https://customer-assets-wrfwihn1.emergentagent.net/job_hpsmun/artifacts/4ce254eb37479c1b_shlok-mun.jpeg",
};
const PHOTO_POS = {
  "Antra": "center 48%",
  "Shlok Walia": "center 72%",
  "Anish Verma": "center 52%",
  "Siya Thakkar": "center 30%",
  "Riyans Anthigari": "center 34%",
  "Karmeest Sonthalia": "center 34%",
  "Saurabh Alivellu": "center 72%",
};

/* ---------------- Data ---------------- */
const committees = [
  { code: "01", short: "DISEC", slug: "disec", full: "Disarmament & International Security", format: "Single Delegate", category: "General Assembly", mandate: "Rethink the architecture of peace and collective security.", agenda: "Regulating autonomous weapons and the new arms race.", status: "Allocations open" },
  { code: "02", short: "JCC", slug: "jcc", full: "Joint Crisis Committee", format: "Double Delegate", category: "Crisis", mandate: "Respond to the unexpected. Build coalitions under pressure.", agenda: "A fast-moving dual-cabinet crisis in real time.", status: "By experience" },
  { code: "03", short: "LOK SABHA", slug: "loksabha", full: "The Lok Sabha", format: "Single Delegate", category: "Indian Politics", mandate: "The house of the people — debate the laws that shape a nation.", agenda: "Landmark legislation and the limits of parliamentary power.", status: "Allocations open" },
  { code: "04", short: "UEFA", slug: "uefa", full: "Union of European Football Associations", format: "Single Delegate", category: "Specialized", mandate: "Where sport, politics and money meet on the same pitch.", agenda: "Financial fair play and the future of European football.", status: "Allocations open" },
  { code: "05", short: "UNODC", slug: "unodc", full: "UN Office on Drugs & Crime", format: "Single Delegate", category: "Specialized", mandate: "Confront transnational crime without losing sight of human rights.", agenda: "Dismantling trafficking networks in a connected world.", status: "Allocations open" },
  { code: "06", short: "UNSC", slug: "unsc", full: "United Nations Security Council", format: "Double Delegate", category: "General Assembly", mandate: "Strategic diplomacy in a chamber where every word carries weight.", agenda: "Maintaining international peace amid emerging flashpoints.", status: "By experience" },
  { code: "07", short: "ICJ", slug: "icj", full: "International Court of Justice", format: "Single Delegate", category: "Judicial", mandate: "A forum for justice, evidence and the weight of international law.", agenda: "Adjudicating a contentious inter-state dispute.", status: "By experience" },
  { code: "08", short: "WHO", slug: "who", full: "World Health Organization", format: "Single Delegate", category: "Specialized", mandate: "Balance science, access and sovereignty in global health.", agenda: "Pandemic preparedness and equitable access to care.", status: "Allocations open" },
  { code: "09", short: "ECOSOC", slug: "ecosoc", full: "Economic & Social Council", format: "Single Delegate", category: "General Assembly", mandate: "Build consensus on the economics of a fairer world.", agenda: "Sustainable development and the global debt crisis.", status: "Allocations open" },
  { code: "10", short: "IP", slug: "ip", full: "International Press", format: "Single Delegate", category: "Press", mandate: "Find the story between the speeches, the corridors and the caucuses.", agenda: "Reporting the conference as it unfolds.", status: "Allocations open" },
];
const bentoSpans = [7, 5, 4, 4, 4, 5, 7, 4, 4, 4];

const schedule = {
  day1: {
    label: "Day One", date: "Saturday · 21 November 2026", theme: "The Chamber Opens",
    items: [
      ["08:00", "Arrival & Registration", "Main Portico · Begumpet", "Collect your delegate credentials, kit and committee room assignment."],
      ["09:00", "Opening Ceremony", "Assembly Hall", "Welcome address, conference briefing and the first roll call."],
      ["10:00", "Committee Session I", "Committee Rooms", "Set the agenda. Make your opening speech. Find your bloc."],
      ["13:00", "Lunch & Delegate Mixer", "The Quadrangle", "A pause to meet the people behind the placards."],
      ["14:00", "Committee Session II", "Committee Rooms", "Moderated caucuses, working papers and the first hard questions."],
      ["17:00", "Day One Close", "Committee Rooms", "A final update from the dais before tomorrow's decisions."],
    ],
  },
  day2: {
    label: "Day Two", date: "Sunday · 22 November 2026", theme: "The Voice Carries",
    items: [
      ["08:30", "Committee Rooms Open", "Committee Rooms", "Return to the floor with sharper arguments and stronger alliances."],
      ["09:00", "Committee Session III", "Committee Rooms", "Move from debate to solutions, amendments and draft resolutions."],
      ["12:30", "Lunch Break", "The Quadrangle", "Reset, recharge and compare notes across committees."],
      ["13:30", "Final Session", "Committee Rooms", "Close debate. Cast your vote. Let the room remember your voice."],
      ["16:00", "Closing Ceremony", "Assembly Hall", "Awards, acknowledgements and the final word from Edition II."],
      ["17:30", "Conference Close", "Main Portico", "Until the next time ideas meet in the same room."],
    ],
  },
};

const principles = [
  ["I", "Vigilance", "Vigilantia", "Stay alert to the world as it is — read the room, read the record, read between the lines."],
  ["II", "Vision", "Visio", "Imagine the world as it could be, then argue your way toward it with evidence and nerve."],
  ["III", "Voice", "Vox", "Have the courage to say what matters — and the discipline to change your mind in public."],
];

const conferenceDocs = [
  ["Delegate Handbook", "Everything you need before you walk into the chamber — procedure, format and conduct.", FileText],
  ["Conference Policies", "Code of conduct, dress code, awards criteria and the rules of procedure.", ShieldCheck],
];

const secretariat = {
  leadership: [
    { role: "Co-Secretary-General", name: "Aaryaveer Gupta", bio: "As Co-Secretary-General, Aaryaveer leads the vision and academic direction of Edition II, overseeing every committee from agenda to final gavel." },
    { role: "Co-Secretary-General", name: "Akshaya Tummala", bio: "As Co-Secretary-General, Akshaya steers the conference's academic standard and delegate experience, ensuring every room runs with rigour and fairness." },
    { role: "Deputy Secretary-General", name: "Sai Advaith Voopalanchi", bio: "As Deputy Secretary-General, Advaith supports the academic vision across every committee and keeps the secretariat moving in step." },
    { role: "Director-General", name: "Sharanya Varshney", bio: "As Director-General, Sharanya oversees the committees and the executive board, translating the vision of Edition II into the work of the floor." },
    { role: "Director-General", name: "Saurabh Alivellu", bio: "As Director-General, Saurabh directs committee delivery and the delegate experience, making sure the two days run seamlessly from first roll call to the final vote." },
    { role: "Charg\u00e9 d'Affaires", name: "Laksh Mahankali", bio: "As Charg\u00e9 d'Affaires, Laksh manages conference affairs and the moving parts behind the scenes, keeping Edition II on course throughout." },
  ],
  board: [
    ["USG · Registrations & Delegate Affairs", "Siya Thakkar"],
    ["USG · Socials", "Riyans Anthigari"],
    ["USG · Policy", "Antra"],
    ["USG · Design", "Jitisha"],
    ["OC Head", "Karmeest Sonthalia"],
    ["USG · Logistics & Hospitality", "Shlok Walia"],
    ["USG · Technology", "Aakash Nambiar"],
    ["USG · Finance", "Anish Verma"],
  ],
};
const initials = (n) => n.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

const faqs = [
  ["When and where is HPS MUN Edition II?", "The conference convenes across two days — 21 to 22 November 2026 — at The Hyderabad Public School, Begumpet, Hyderabad."],
  ["Is prior MUN experience required?", "No. We welcome both first-time and experienced delegates. Several committees are designed specifically with beginners in mind."],
  ["How are committee allocations decided?", "Allocations consider your committee preferences, experience and availability. You indicate your choices during registration."],
  ["What should a delegate bring?", "Formal business attire, a notebook, pens and your research. Everything else is provided on-site."],
  ["How do I register?", "Registration runs through our official form — use the Register button anywhere on this site to begin. Allocations are confirmed by the secretariat after you sign up."],
];

/* ---------------- Primitives ---------------- */
function useCountdown() {
  const target = useMemo(() => new Date("2026-11-21T09:00:00+05:30").getTime(), []);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  const diff = Math.max(0, target - now);
  return { days: Math.floor(diff / 86400000), hours: Math.floor(diff / 3600000) % 24, minutes: Math.floor(diff / 60000) % 60, seconds: Math.floor(diff / 1000) % 60 };
}

function Reveal({ children, className = "", delay = 0, y = 30 }) {
  return <motion.div className={className} initial={{ opacity: 0, y }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}>{children}</motion.div>;
}

/* Architectural blueprint photo plate */
function BlueprintPlate({ code = "◆", label = "ARCHIVE PLATE", ratio = "16/10", className = "" }) {
  return (
    <div className={`plate ${className}`} style={{ aspectRatio: ratio }} data-testid="photo-placeholder" aria-hidden="true">
      <span className="plate-tick tl" /><span className="plate-tick tr" /><span className="plate-tick bl" /><span className="plate-tick br" />
      <div className="plate-hatch" />
      <div className="plate-cross" />
      <div className="plate-center"><span className="plate-numeral">{code}</span></div>
      <span className="plate-label">{label} · {code}</span>
      <span className="plate-coord">24.23°N / 78.47°E</span>
    </div>
  );
}

const LOGO = "https://customer-assets-wrfwihn1.emergentagent.net/job_hpsmun/artifacts/cfb33d63a34fbf3f_HPSMUN%20NEW%20LOGO.jpeg";
function Logo({ size = 44 }) {
  return <img src={LOGO} alt="HPS MUN crest" width={size} height={size} className="logo-mark" style={{ width: size, height: size }} />;
}

function Loader() {
  const [done, setDone] = useState(false);
  useEffect(() => { const t = setTimeout(() => setDone(true), 1600); return () => clearTimeout(t); }, []);
  return (
    <AnimatePresence>
      {!done && (
        <motion.div className="loader" initial={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.6 } }} data-testid="loader">
          <motion.div initial={{ opacity: 0, scale: 0.85, rotate: -20 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}><Logo size={72} /></motion.div>
          <div className="loader-word">HPS MUN</div>
          <div className="loader-bar"><motion.span initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1.4, ease: "easeInOut" }} /></div>
          <div className="loader-note">CONVENING · EDITION II</div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const x = useSpring(scrollYProgress, { stiffness: 120, damping: 28, restDelta: 0.001 });
  return <motion.div className="scroll-progress" style={{ scaleX: x }} aria-hidden="true" />;
}

function GoldCursor() {
  const x = useMotionValue(-300); const y = useMotionValue(-300);
  const sx = useSpring(x, { stiffness: 150, damping: 26 }); const sy = useSpring(y, { stiffness: 150, damping: 26 });
  useEffect(() => { const move = (e) => { x.set(e.clientX); y.set(e.clientY); }; window.addEventListener("pointermove", move); return () => window.removeEventListener("pointermove", move); }, [x, y]);
  return <motion.div className="gold-cursor" style={{ left: sx, top: sy }} aria-hidden="true" />;
}

/* ---------------- Header ---------------- */
function Header({ onRegister }) {
  const [open, setOpen] = useState(false);
  const [solid, setSolid] = useState(false);
  const location = useLocation();
  useEffect(() => { const onScroll = () => setSolid(window.scrollY > 36); onScroll(); window.addEventListener("scroll", onScroll); return () => window.removeEventListener("scroll", onScroll); }, []);
  const go = (id) => { setOpen(false); if (location.pathname !== "/") window.location.href = `/#${id}`; else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); };
  return (
    <motion.header className={solid || location.pathname !== "/" ? "site-header solid" : "site-header"} initial={{ y: -90 }} animate={{ y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} data-testid="site-header">
      <Link to="/" className="wordmark" data-testid="home-link" onClick={() => setOpen(false)}>
        <Logo size={38} />
        <span className="wordmark-text">HPS MUN<small>BEGUMPET · ED. II</small></span>
      </Link>
      <nav className={open ? "nav-links is-open" : "nav-links"} data-testid="primary-navigation">
        <Link to="/" onClick={() => setOpen(false)} data-testid="nav-home">Home</Link>
        <button onClick={() => go("conference")} data-testid="nav-conference">Conference</button>
        <Link to="/committees" onClick={() => setOpen(false)} data-testid="nav-committees">Committees</Link>
        <Link to="/schedule" onClick={() => setOpen(false)} data-testid="nav-schedule">Schedule</Link>
        <Link to="/secretariat" onClick={() => setOpen(false)} data-testid="nav-secretariat">Secretariat</Link>
        <button onClick={() => go("faq")} data-testid="nav-faq">FAQ</button>
        <button onClick={() => { setOpen(false); onRegister(); }} className="nav-cta" data-testid="nav-register">Register <ArrowUpRight size={14} /></button>
      </nav>
      <button className="menu-toggle" onClick={() => setOpen(!open)} aria-label="Toggle navigation" data-testid="mobile-menu-button">{open ? <X /> : <Menu />}</button>
    </motion.header>
  );
}

/* ---------------- Hero ---------------- */
function CountdownDocket() {
  const t = useCountdown();
  const cells = [[t.days, "Days"], [t.hours, "Hours"], [t.minutes, "Min"], [t.seconds, "Sec"]];
  return (
    <div className="countdown-docket" data-testid="countdown-docket">
      <span className="docket-label">Chamber convenes in</span>
      <div className="docket-cells">
        {cells.map(([v, l]) => (
          <div key={l}><strong>{String(v).padStart(2, "0")}</strong><span>{l}</span></div>
        ))}
      </div>
    </div>
  );
}

function CodeRoll() {
  const roll = [...committees, ...committees];
  return (
    <div className="code-roll" aria-hidden="true">
      <motion.div className="code-roll-track" animate={{ y: ["0%", "-50%"] }} transition={{ duration: 16, repeat: Infinity, ease: "linear" }}>
        {roll.map((c, i) => (
          <div className="code-roll-item" key={i}><span>{c.code}</span><strong>{c.short}</strong><em>{c.category}</em></div>
        ))}
      </motion.div>
    </div>
  );
}

function Hero({ onRegister }) {
  const { scrollY } = useScroll();
  const fade = useTransform(scrollY, [0, 500], [1, 0]);
  const words = ["HPS", "MUN"];
  return (
    <section className="hero" data-testid="hero-section">
      <motion.div className="hero-grid" style={{ opacity: fade }}>
        <div className="hero-left">
          <motion.p className="ledger-eyebrow" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.7 }}>
            Decree No. 02 — The Hyderabad Public School · Begumpet
          </motion.p>
          <h1 className="hero-title">
            <span className="hero-line">
              {words.map((w, i) => (
                <span className="hero-word" key={w}><motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ delay: 0.3 + i * 0.1, duration: 0.85, ease: [0.22, 1, 0.36, 1] }}>{w}</motion.span></span>
              ))}
            </span>
            <motion.span className="hero-edition-line" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.8 }}>Edition <em>II</em></motion.span>
          </h1>
          <motion.p className="hero-theme" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}>
            <em>Vigilance. Vision. Voice.</em> A two-day assembly for students ready to take responsibility for the questions that shape our world.
          </motion.p>
          <motion.div className="hero-actions" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.82, duration: 0.7 }}>
            <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} className="button button-gold" onClick={onRegister} data-testid="hero-register-button">Register <MoveRight size={16} /></motion.button>
            <button className="charter-link" onClick={() => document.getElementById("conference")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-charter-link">Read the conference charter <ArrowUpRight size={14} /></button>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.7 }}><CountdownDocket /></motion.div>
        </div>
        <motion.aside className="hero-docket" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }} data-testid="hero-docket">
          <div className="docket-head">
            <Logo size={40} />
            <div><span className="ledger-eyebrow nomargin">Diplomatic Docket</span><strong>Official Secretariat Record</strong></div>
          </div>
          <div className="docket-dates">
            <div><span>Convenes</span><strong>21 NOV 2026</strong></div>
            <div><span>Concludes</span><strong>22 NOV 2026</strong></div>
          </div>
          <div className="docket-arenas">
            <span className="ledger-eyebrow nomargin">Ten Sovereign Arenas</span>
            <CodeRoll />
          </div>
          <div className="docket-foot">
            <span>Verification</span><em>ED-II / 2026 / HYD</em>
          </div>
        </motion.aside>
      </motion.div>
      <div className="hero-scroll"><ChevronDown size={16} /> Scroll to convene</div>
    </section>
  );
}

/* ---------------- Sections ---------------- */
function SectionTag({ n, children, light }) {
  return <span className={light ? "section-tag light" : "section-tag"}><i>{n}</i> {children}</span>;
}

function Conference() {
  return (
    <section className="section conference" id="conference" data-testid="conference-section">
      <div className="section-head"><SectionTag n="01">The Conference Charter</SectionTag></div>
      <div className="charter-grid">
        <Reveal><h2 className="display">Where ideas<br /><em>take a stand.</em></h2></Reveal>
        <Reveal delay={0.12} className="charter-copy">
          <p>HPS MUN: Edition II is a two-day conference for students ready to engage with the questions that shape our world — hosted in the halls of The Hyderabad Public School, Begumpet.</p>
          <p>Guided by <strong>vigilance, vision and voice</strong>, delegates debate, collaborate and lead. Diplomacy here is not a performance; it is attempted honestly, by people willing to be wrong in public and change their minds anyway.</p>
          <a href="mailto:hpsmun2025@gmail.com" className="text-link" data-testid="contact-email-link">Contact the conference secretariat <ArrowUpRight size={14} /></a>
        </Reveal>
      </div>
      <div className="principles">
        {principles.map(([num, title, latin, copy], i) => (
          <Reveal key={num} delay={i * 0.1}>
            <div className="principle">
              <span className="principle-num">{num}</span>
              <h3 className="display">{title}</h3>
              <span className="principle-latin">— {latin}</span>
              <p>{copy}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function CommitteesBento() {
  return (
    <section className="section committees-bento" data-testid="committees-section">
      <div className="section-head row">
        <SectionTag n="02">The Ten Arenas</SectionTag>
        <Link to="/committees" className="ghost-link" data-testid="all-committees-link">Enter the committee ledger <ArrowUpRight size={15} /></Link>
      </div>
      <Reveal><h2 className="display big">Choose your <em>arena.</em></h2></Reveal>
      <div className="bento">
        {committees.map((c, i) => {
          const span = bentoSpans[i];
          const feature = span >= 5;
          return (
            <motion.div key={c.short} className={`bento-card c-${span}`} initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.06, duration: 0.5 }}>
              <Link to="/committees" className="bento-inner" data-testid={`committee-card-${c.slug}`}>
                {feature && <BlueprintPlate code={c.code} label="ARENA" ratio={span >= 7 ? "21/9" : "16/9"} className="bento-plate" />}
                <div className="bento-body">
                  <div className="bento-top"><span className="bento-code">{c.code}</span><span className="pill">{c.category}</span></div>
                  <h3 className="display">{c.short}</h3>
                  <p className="bento-full">{c.full}</p>
                  <p className="bento-mandate">{c.mandate}</p>
                  <span className="bento-enter">Enter arena <MoveRight size={14} /></span>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function ScheduleTeaser() {
  return (
    <section className="section schedule-teaser" data-testid="schedule-teaser">
      <div className="section-head"><SectionTag n="03">The Order of Proceedings</SectionTag></div>
      <div className="teaser-grid">
        <Reveal><h2 className="display">Two days.<br /><em>One chamber.</em></h2></Reveal>
        <Reveal delay={0.12} className="teaser-copy">
          <p>From first roll call to the final gavel, study the rhythm of Edition II before you arrive — session by session, hour by hour.</p>
          <Link to="/schedule" className="button button-gold" data-testid="schedule-teaser-link">View the full schedule <MoveRight size={16} /></Link>
        </Reveal>
      </div>
      <div className="ribbon">
        <div className="ribbon-cell"><span>21 NOV · 09:00</span><strong>Opening Ceremony</strong><em>Assembly Hall</em></div>
        <div className="ribbon-cell"><span>21 NOV · 10:00</span><strong>Committee Session I</strong><em>Committee Rooms</em></div>
        <div className="ribbon-cell"><span>22 NOV · 13:30</span><strong>Final Session</strong><em>Committee Rooms</em></div>
        <div className="ribbon-cell"><span>22 NOV · 16:00</span><strong>Closing Ceremony</strong><em>Assembly Hall</em></div>
      </div>
    </section>
  );
}

function Archive() {
  return (
    <section className="section archive" id="archive" data-testid="archive-section">
      <div className="section-head"><SectionTag n="04">The Conference Archive</SectionTag></div>
      <div className="archive-card">
        <BlueprintPlate code="I" label="ARCHIVE PLATE" ratio="4/3" className="archive-plate" />
        <Reveal className="archive-copy">
          <h2 className="display">Build the<br /><em>archive.</em></h2>
          <p>Previous editions, delegate moments and the ideas that stayed with us. This archive awaits its first chapter — Edition I lives on, and Edition II will be written by you.</p>
          <span className="archive-note">Previous editions · Editable archive</span>
        </Reveal>
      </div>
    </section>
  );
}

function Resources() {
  const [available, setAvailable] = useState({});
  useEffect(() => {
    fetch(`${API}/api/resources`).then((r) => r.json()).then((list) => {
      const map = {}; (list || []).forEach((x) => { map[x.slot] = x; }); setAvailable(map);
    }).catch(() => {});
  }, []);
  const anyAvailable = Object.keys(available).length > 0;
  return (
    <section className="section resources" id="resources" data-testid="resources-section">
      <div className="section-head row"><SectionTag n="05">Delegate Resources</SectionTag><span className="soon-pill">{anyAvailable ? "Downloads open" : "Coming soon"}</span></div>
      <Reveal><h2 className="display">Prepare for<br /><em>the floor.</em></h2></Reveal>
      <div className="doc-grid">
        {conferenceDocs.map(([title, copy, Icon], i) => {
          const key = i === 0 ? "handbook" : "policies";
          const file = available[key];
          return (
            <Reveal key={title} delay={i * 0.08}>
              <div className="doc-card" data-testid={`resource-${key}`}>
                <div className="doc-head"><Icon size={22} className="resource-icon" /><span className="doc-format">{file ? "PDF · Available" : "PDF · Awaiting release"}</span></div>
                <h3 className="display">{title}</h3>
                <p>{copy}</p>
                {file ? (
                  <a className="dl-btn ready" href={`${API}/api/resources/${key}/download`} target="_blank" rel="noreferrer" data-testid={`download-${key}`}><Download size={13} /> Download PDF</a>
                ) : (
                  <button className="dl-btn locked" disabled data-testid={`download-${key}`}><Lock size={13} /> Locked · Coming soon</button>
                )}
              </div>
            </Reveal>
          );
        })}
      </div>
      <div className="guides-head"><span className="ledger-eyebrow nomargin">Background Guides · One dossier per committee</span></div>
      <div className="guides-grid" data-testid="background-guides">
        {committees.map((c, i) => {
          const slot = `guide-${c.slug}`;
          const file = available[slot];
          const Card = file ? motion.a : motion.div;
          const linkProps = file ? { href: `${API}/api/resources/${slot}/download`, target: "_blank", rel: "noreferrer" } : {};
          return (
            <Card key={c.short} className={file ? "guide-card ready" : "guide-card"} {...linkProps} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 5) * 0.05, duration: 0.45 }} data-testid={`guide-${c.slug}`}>
              <div className="guide-top"><span className="guide-code">{c.code}</span>{file ? <Download size={13} className="guide-lock" /> : <Lock size={13} className="guide-lock" />}</div>
              <h4 className="display">{c.short}</h4>
              <small>{c.full}</small>
              <span className="guide-status">{file ? <><Download size={12} /> Download</> : <><Download size={12} /> Coming soon</>}</span>
            </Card>
          );
        })}
      </div>
    </section>
  );
}

function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section className="section faq" id="faq" data-testid="faq-section">
      <div className="section-head"><SectionTag n="06">Points of Information</SectionTag></div>
      <div className="faq-grid">
        <Reveal><h2 className="display">Before you<br /><em>arrive.</em></h2></Reveal>
        <div className="faq-list" data-testid="faq-list">
          {faqs.map(([q, a], i) => (
            <div key={i} className={open === i ? "faq-item open" : "faq-item"} data-testid={`faq-item-${i}`}>
              <button onClick={() => setOpen(open === i ? -1 : i)} data-testid={`faq-toggle-${i}`}>
                <span>{q}</span>{open === i ? <Minus size={18} /> : <Plus size={18} />}
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div className="faq-answer" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                    <p>{a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Register({ onRegister }) {
  return (
    <section className="section register" data-testid="registration-section">
      <div className="register-inner">
        <Reveal>
          <p className="ledger-eyebrow center">Registration · Edition II</p>
          <h2 className="display big">Your next chapter<br /><em>starts here.</em></h2>
        </Reveal>
        <Reveal delay={0.12} className="register-cta">
          <p>Secure your place at HPS MUN Edition II. Registration runs through our official form — tap below to begin.</p>
          <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} onClick={onRegister} className="button button-gold" data-testid="open-registration-button">Register <ArrowUpRight size={16} /></motion.button>
        </Reveal>
      </div>
    </section>
  );
}

function SecretariatPinned() {
  return (
    <section className="section pinned-section" data-testid="secretariat-pinned">
      <div className="section-head"><SectionTag n="·">From the Secretariat</SectionTag></div>
      <Reveal>
        <div className="pinned-card">
          <span className="pin"><Pin size={17} /></span>
          <p className="pinned-eyebrow">Pinned · A note to delegates</p>
          <p className="pinned-quote">"Diplomacy here is not a performance. The delegates who leave having changed their mind at least once are the ones who did the real work — so come ready to listen as hard as you argue."</p>
          <div className="pinned-foot">
            <div className="pinned-sign"><strong>Aaryaveer Gupta &amp; Akshaya Tummala</strong><span>Co-Secretaries-General · Edition II</span></div>
            <Link to="/secretariat" className="button button-gold" data-testid="pinned-secretariat-link">Meet the secretariat <MoveRight size={16} /></Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

function Home({ onRegister }) {
  return (
    <main>
      <Hero onRegister={onRegister} />
      <Conference />
      <CommitteesBento />
      <SecretariatPinned />
      <ScheduleTeaser />
      <Archive />
      <Resources />
      <Faq />
      <Register onRegister={onRegister} />
    </main>
  );
}

/* ---------------- Committees Page ---------------- */
function Committees() {
  const categories = ["All", ...Array.from(new Set(committees.map((c) => c.category)))];
  const [filter, setFilter] = useState("All");
  const list = filter === "All" ? committees : committees.filter((c) => c.category === filter);
  const [selected, setSelected] = useState(committees[0]);
  const [available, setAvailable] = useState({});
  useEffect(() => { if (!list.includes(selected)) setSelected(list[0]); }, [filter]); // eslint-disable-line
  useEffect(() => {
    fetch(`${API}/api/resources`).then((r) => r.json()).then((l) => {
      const m = {}; (l || []).forEach((x) => { m[x.slot] = x; }); setAvailable(m);
    }).catch(() => {});
  }, []);
  const guideSlot = `guide-${selected.slug}`;
  const matrixSlot = `matrix-${selected.slug}`;
  const guideFile = available[guideSlot];
  const matrixFile = available[matrixSlot];
  return (
    <main className="inner-page committees-page" data-testid="committees-page">
      <div className="inner-hero">
        <p className="ledger-eyebrow">The Committee Ledger · Edition II</p>
        <h1 className="display">Choose your <em>arena.</em></h1>
        <p className="lead">Ten sovereign committees. Select one to open its dossier and find the question that will become yours.</p>
      </div>
      <div className="filter-row" data-testid="committee-filters">
        {categories.map((cat) => (
          <button key={cat} className={filter === cat ? "filter-chip active" : "filter-chip"} onClick={() => setFilter(cat)} data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}>{cat}</button>
        ))}
      </div>
      <div className="committee-directory">
        <div className="committee-list" data-testid="committee-directory">
          {list.map((item, i) => (
            <motion.button initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }} className={selected.short === item.short ? "directory-item active" : "directory-item"} onClick={() => setSelected(item)} key={item.short} data-testid={`directory-${item.slug}`}>
              <span>{item.code}</span>
              <span className="directory-name"><strong>{item.short}</strong><small>{item.full}</small></span>
              <ArrowUpRight size={16} />
            </motion.button>
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.article key={selected.short} className="committee-detail" data-testid="committee-detail" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }}>
            <BlueprintPlate code={selected.code} label="DOSSIER" ratio="21/9" className="detail-plate" />
            <div className="detail-body">
              <div className="detail-top"><span className="pill">{selected.category}</span><span className="pill ghost">{selected.format}</span><span className="status-pill">{selected.status}</span></div>
              <h2 className="display">{selected.full}</h2>
              <p className="detail-agenda"><span>Agenda —</span> {selected.agenda}</p>
              <p className="detail-mandate">{selected.mandate}</p>
              <div className="detail-actions">
                {guideFile ? (
                  <a className="button button-gold" href={`${API}/api/resources/${guideSlot}/download`} target="_blank" rel="noreferrer" data-testid="committee-guide-button"><Download size={15} /> Background guide</a>
                ) : (
                  <button className="button button-ghost" disabled data-testid="committee-guide-button"><Lock size={14} /> Background guide · soon</button>
                )}
                {matrixFile ? (
                  <a className="button button-ghost ready" href={`${API}/api/resources/${matrixSlot}/download`} target="_blank" rel="noreferrer" data-testid="committee-matrix-button"><Download size={15} /> Allocation matrix</a>
                ) : (
                  <button className="button button-ghost" disabled data-testid="committee-matrix-button"><Lock size={14} /> Allocation matrix · soon</button>
                )}
              </div>
            </div>
          </motion.article>
        </AnimatePresence>
      </div>
    </main>
  );
}

/* ---------------- Schedule Page ---------------- */
function Schedule() {
  const [day, setDay] = useState("day1"); const active = schedule[day];
  return (
    <main className="inner-page schedule-page" data-testid="schedule-page">
      <div className="inner-hero">
        <p className="ledger-eyebrow">The Order of Proceedings · 21—22 Nov 2026</p>
        <h1 className="display">Make time <em>for ideas.</em></h1>
        <p className="lead">A working rhythm for two days of debate, collaboration and decisions in the halls of Begumpet.</p>
      </div>
      <div className="schedule-controls" data-testid="schedule-day-switcher">
        {Object.entries(schedule).map(([key, value]) => (
          <button className={day === key ? "day-button active" : "day-button"} onClick={() => setDay(key)} key={key} data-testid={`schedule-${key}-button`}>
            <span>{key === "day1" ? "I" : "II"}</span>
            <span className="day-text"><strong className="display">{value.label}</strong><small>{value.date}</small></span>
          </button>
        ))}
      </div>
      <motion.section key={day} className="schedule-board" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} data-testid="schedule-board">
        <div className="schedule-board-head">
          <div><p className="ledger-eyebrow nomargin">{active.date}</p><h2 className="display">{active.theme}</h2></div>
          <Logo size={40} />
        </div>
        <div className="timeline">
          {active.items.map(([time, title, location, detail], i) => (
            <motion.div className="timeline-item" key={title} initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }} data-testid={`schedule-item-${i + 1}`}>
              <time>{time}<small>IST</small></time>
              <div className="timeline-rail"><span className="timeline-dot" /></div>
              <div className="timeline-copy"><h3 className="display">{title}</h3><span className="timeline-loc">{location}</span><p>{detail}</p></div>
            </motion.div>
          ))}
        </div>
      </motion.section>
      <p className="schedule-footnote">Timings shown are a planning guide and will be confirmed by the conference secretariat.</p>
    </main>
  );
}

/* ---------------- Registration Modal ---------------- */
function RegistrationModal({ close }) {
  const submit = (e) => { e.preventDefault(); toast.success("Credentials request noted", { description: "The HPS MUN secretariat will share the official form link soon." }); close(); };
  return (
    <div className="modal-backdrop" onMouseDown={close} data-testid="registration-modal">
      <motion.div className="registration-modal" initial={{ opacity: 0, scale: 0.94, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} onMouseDown={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={close} aria-label="Close registration" data-testid="registration-close"><X /></button>
        <Logo size={38} />
        <p className="ledger-eyebrow">Take your place</p>
        <h2 className="display">Begin your<br /><em>next chapter.</em></h2>
        <p className="modal-copy">The official registration form is being prepared. Leave your details and the conference secretariat will be in touch.</p>
        <form onSubmit={submit} data-testid="registration-form">
          <label>Name<input required name="name" placeholder="Your full name" data-testid="registration-name" /></label>
          <label>Email<input required type="email" name="email" placeholder="you@example.com" data-testid="registration-email" /></label>
          <label>Delegation / School<input name="school" placeholder="Your school or delegation" data-testid="registration-school" /></label>
          <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} className="button button-gold full-width" type="submit" data-testid="registration-submit">Request registration information <MoveRight size={16} /></motion.button>
        </form>
      </motion.div>
    </div>
  );
}

function Secretariat() {
  return (
    <main className="inner-page secretariat-page" data-testid="secretariat-page">
      <div className="inner-hero">
        <p className="ledger-eyebrow">The Office of the Secretariat · Edition II</p>
        <h1 className="display">The <em>Secretariat.</em></h1>
        <p className="lead">The team convening Edition II — the voices behind the dais, the dockets and the decisions. Portraits are placeholders, ready for the official team.</p>
      </div>

      {secretariat.leadership.map((person, i) => {
        const photo = PHOTOS[person.name];
        return (
          <section className={i % 2 === 1 ? "letter-block reverse" : "letter-block"} key={person.name} data-testid={`leader-${i}`}>
            <Reveal className="letter-portrait">
              {photo ? (
                <div className="lead-photo"><img src={photo} alt={person.name} loading="lazy" style={{ objectPosition: PHOTO_POS[person.name] || "center 20%" }} /></div>
              ) : (
                <BlueprintPlate code={initials(person.name)} label="PORTRAIT" ratio="3/4" />
              )}
            </Reveal>
            <Reveal delay={0.1} className="letter-body">
              <p className="ledger-eyebrow">{person.role}</p>
              <h2 className="display lead-name">{person.name}</h2>
              <p className="letter-writeup">{person.bio}</p>
            </Reveal>
          </section>
        );
      })}

      <section className="board" data-testid="executive-board">
        <div className="section-head"><SectionTag n="·">The Executive Board</SectionTag></div>
        <div className="board-grid">
          {secretariat.board.map(([role, name], i) => {
            const photo = PHOTOS[name];
            return (
              <motion.div className="board-card" key={name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.06, duration: 0.45 }} data-testid={`board-member-${i}`}>
                {photo ? (
                  <div className="board-photo"><img src={photo} alt={name} loading="lazy" style={{ objectPosition: PHOTO_POS[name] || "center 22%" }} /></div>
                ) : (
                  <BlueprintPlate code={initials(name)} label="PORTRAIT" ratio="1/1" className="board-plate" />
                )}
                <div className="board-body"><h3 className="display">{name}</h3><span>{role}</span></div>
              </motion.div>
            );
          })}
        </div>
      </section>
    </main>
  );
}

function AdminResources() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [items, setItems] = useState({});
  const [busy, setBusy] = useState("");
  const load = () => fetch(`${API}/api/resources`).then((r) => r.json()).then((list) => {
    const m = {}; (list || []).forEach((x) => { m[x.slot] = x; }); setItems(m);
  }).catch(() => {});
  useEffect(() => { load(); }, []);
  const rows = [
    ["handbook", "Delegate Handbook"],
    ["policies", "Conference Policies"],
    ...committees.flatMap((c) => [
      [`guide-${c.slug}`, `${c.short} — Background Guide`],
      [`matrix-${c.slug}`, `${c.short} — Allocation Matrix`],
    ]),
  ];
  const upload = async (slot, input) => {
    const f = input.files[0]; if (!f) return;
    setBusy(slot);
    const fd = new FormData(); fd.append("file", f);
    try {
      const res = await fetch(`${API}/api/admin/resources/${slot}`, { method: "POST", headers: { "X-Admin-Token": token }, body: fd });
      if (res.ok) { toast.success(`Uploaded ${f.name}`); load(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || `Upload failed (${res.status})`); if (res.status === 401) setAuthed(false); }
    } catch { toast.error("Upload failed — check your connection"); }
    finally { setBusy(""); input.value = ""; }
  };
  const remove = async (slot) => {
    const res = await fetch(`${API}/api/admin/resources/${slot}`, { method: "DELETE", headers: { "X-Admin-Token": token } });
    if (res.ok) { toast.success("File removed"); load(); } else { toast.error("Remove failed"); }
  };
  if (!authed) {
    return (
      <main className="inner-page admin-page" data-testid="admin-page">
        <div className="inner-hero"><p className="ledger-eyebrow">Secretariat Access · Edition II</p><h1 className="display">Resource <em>console.</em></h1><p className="lead">Enter the secretariat passcode to upload delegate handbooks, policies and committee background guides.</p></div>
        <div className="admin-gate">
          <label>Passcode<input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Secretariat passcode" data-testid="admin-token-input" onKeyDown={(e) => { if (e.key === "Enter" && token) setAuthed(true); }} /></label>
          <button className="button button-gold" disabled={!token} onClick={() => setAuthed(true)} data-testid="admin-unlock-button">Unlock console <MoveRight size={16} /></button>
        </div>
      </main>
    );
  }
  return (
    <main className="inner-page admin-page" data-testid="admin-page">
      <div className="inner-hero"><p className="ledger-eyebrow">Secretariat Access · Edition II</p><h1 className="display">Resource <em>console.</em></h1><p className="lead">Upload a file to publish it instantly on the Resources section. Max 25MB · PDF, DOC or image.</p></div>
      <div className="admin-table" data-testid="admin-table">
        {rows.map(([slot, label]) => {
          const file = items[slot];
          return (
            <div className="admin-row" key={slot} data-testid={`admin-row-${slot}`}>
              <div className="admin-row-main">
                <span className={file ? "admin-dot on" : "admin-dot"} />
                <div><strong>{label}</strong><small>{file ? file.original_filename : "No file uploaded"}</small></div>
              </div>
              <div className="admin-row-actions">
                <label className="admin-upload-btn">{busy === slot ? "Uploading…" : file ? "Replace" : "Upload"}
                  <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp" onChange={(e) => upload(slot, e.target)} data-testid={`admin-upload-${slot}`} />
                </label>
                {file && <button className="admin-remove-btn" onClick={() => remove(slot)} data-testid={`admin-remove-${slot}`}>Remove</button>}
              </div>
            </div>
          );
        })}
      </div>
    </main>
  );
}

function Footer() {
  return (
    <footer className="site-footer" data-testid="site-footer">
      <div className="footer-top">
        <div className="footer-brand"><Logo size={34} /><div className="wordmark-text">HPS MUN<small>BEGUMPET · ED. II</small></div></div>
        <p className="footer-motto">Vigilance. Vision. Voice.<br />The Hyderabad Public School, Begumpet · Hyderabad</p>
      </div>
      <div className="footer-links">
        <Link to="/committees" data-testid="footer-committees-link">Committees <ArrowUpRight size={13} /></Link>
        <Link to="/schedule" data-testid="footer-schedule-link">Schedule <ArrowUpRight size={13} /></Link>
        <Link to="/secretariat" data-testid="footer-secretariat-link">Secretariat <ArrowUpRight size={13} /></Link>
        <a href="mailto:hpsmun2025@gmail.com" data-testid="footer-email-link">hpsmun2025@gmail.com <ArrowUpRight size={13} /></a>
      </div>
      <small>© 2026 HPS MUN · Edition II · ED-II / 2026 / HYD</small>
    </footer>
  );
}

function App() {
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const handleRegister = () => {
    if (REGISTER_URL) window.open(REGISTER_URL, "_blank", "noopener");
    else setRegistrationOpen(true);
  };
  return (
    <BrowserRouter>
      <div className="app">
        <Loader />
        <ScrollProgress />
        <GoldCursor />
        <Header onRegister={handleRegister} />
        <Routes>
          <Route path="/" element={<Home onRegister={handleRegister} />} />
          <Route path="/committees" element={<Committees />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/secretariat" element={<Secretariat />} />
          <Route path="/admin" element={<AdminResources />} />
        </Routes>
        <Footer />
        {registrationOpen && <RegistrationModal close={() => setRegistrationOpen(false)} />}
        <Toaster position="bottom-right" theme="dark" richColors />
      </div>
    </BrowserRouter>
  );
}

export default App;
